import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import { Textarea } from '@heyroot/ui/primitives/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heyroot/backoffice-ui';
import {
  fetchKnowledgeEntries,
  upsertKnowledgeEntry,
  disableKnowledgeEntry,
  queryKeys,
  type KnowledgeRow,
  type UpsertKnowledgeBody,
} from '../lib/api';

const TIERS = ['high', 'medium', 'low'] as const;
const TIER_LABELS: Record<string, string> = {
  high: 'High (direct answer)',
  medium: 'Medium (answer + note)',
  low: 'Low (escalate)',
};

const EMPTY_FORM: UpsertKnowledgeBody = {
  tenantId: '',
  category: 'assistant-usage',
  entryKey: '',
  questionVariants: [''],
  answer: '',
  confidenceTier: 'high',
  confidenceThreshold: 0.8,
};

export function KnowledgeEditorPage() {
  const [tenantId, setTenantId] = useState('');
  const [committed, setCommitted] = useState('');
  const [form, setForm] = useState<UpsertKnowledgeBody>(EMPTY_FORM);
  const [editing, setEditing] = useState<KnowledgeRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const fieldId = useId();

  const qc = useQueryClient();

  const { data: entries = [], isLoading } = useQuery({
    queryKey: queryKeys.knowledgeEntries(committed),
    queryFn: () => fetchKnowledgeEntries(committed),
    enabled: committed.length > 0,
  });

  const upsertMutation = useMutation({
    mutationFn: upsertKnowledgeEntry,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.knowledgeEntries(committed) });
      setFormOpen(false);
      setForm({ ...EMPTY_FORM, tenantId: committed });
      setEditing(null);
    },
  });

  const disableMutation = useMutation({
    mutationFn: disableKnowledgeEntry,
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.knowledgeEntries(committed) }),
  });

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM, tenantId: committed });
    setFormOpen(true);
  }

  function openEdit(row: KnowledgeRow) {
    setEditing(row);
    setForm({
      tenantId: row.tenantId,
      category: row.category,
      entryKey: row.entryKey,
      questionVariants: row.questionVariants,
      answer: row.answer,
      confidenceTier: row.confidenceTier as 'high' | 'medium' | 'low',
      confidenceThreshold: row.confidenceThreshold,
      sourceEntryKey: row.sourceEntryKey,
    });
    setFormOpen(true);
  }

  function handleVariantChange(idx: number, val: string) {
    const next = [...form.questionVariants];
    next[idx] = val;
    setForm((f) => ({ ...f, questionVariants: next }));
  }

  function addVariant() {
    setForm((f) => ({ ...f, questionVariants: [...f.questionVariants, ''] }));
  }

  function removeVariant(idx: number) {
    setForm((f) => ({
      ...f,
      questionVariants: f.questionVariants.filter((_, i) => i !== idx),
    }));
  }

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-heading-2 text-primary mb-4">Knowledge Base Editor</h1>

      {/* Tenant selector */}
      <div className="flex gap-2 mb-6">
        <Input
          placeholder="Tenant ID"
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          className="w-72"
          aria-label="Tenant ID"
        />
        <Button
          variant="secondary"
          onClick={() => setCommitted(tenantId)}
          disabled={!tenantId.trim()}
        >
          Load
        </Button>
      </div>

      {committed && (
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-body-sm text-muted">
              {isLoading ? 'Loading…' : `${entries.length} entries`}
            </p>
            <Button onClick={openCreate} size="sm">
              + New entry
            </Button>
          </div>

          {/* Entry list */}
          <div className="border border-border rounded-control overflow-hidden">
            <table className="w-full text-body-sm">
              <thead>
                <tr className="border-b border-border bg-surface-sunken">
                  <th className="text-left px-3 py-2 text-table-header uppercase text-secondary">
                    Entry key
                  </th>
                  <th className="text-left px-3 py-2 text-table-header uppercase text-secondary">
                    Category
                  </th>
                  <th className="text-left px-3 py-2 text-table-header uppercase text-secondary">
                    Tier
                  </th>
                  <th className="text-left px-3 py-2 text-table-header uppercase text-secondary">
                    Status
                  </th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {entries.map((row) => (
                  <tr key={row.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{row.entryKey}</td>
                    <td className="px-3 py-2">{row.category}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded text-caption ${
                          row.confidenceTier === 'high'
                            ? 'bg-feedback-success-subtle text-feedback-success'
                            : row.confidenceTier === 'medium'
                              ? 'bg-feedback-warning-subtle text-feedback-warning'
                              : 'bg-feedback-error-subtle text-feedback-error'
                        }`}
                      >
                        {row.confidenceTier}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={row.status === 'active' ? 'text-feedback-success' : 'text-muted'}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => openEdit(row)}
                        className="mr-3"
                      >
                        Edit
                      </Button>
                      {row.status === 'active' && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => disableMutation.mutate(row.id)}
                          disabled={disableMutation.isPending}
                        >
                          Disable
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {!isLoading && entries.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-muted">
                      No entries yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Editor form */}
      {formOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={editing ? 'Edit entry' : 'New entry'}
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div className="bg-surface rounded-control shadow-lg w-full max-w-lg p-6 overflow-y-auto max-h-[90vh]">
            <h2 className="text-heading-3 text-primary mb-4">
              {editing ? 'Edit entry' : 'New entry'}
            </h2>

            <div className="space-y-3">
              <div>
                <label
                  htmlFor={`${fieldId}-entry-key`}
                  className="text-label text-secondary block mb-1"
                >
                  Entry key
                </label>
                <Input
                  id={`${fieldId}-entry-key`}
                  value={form.entryKey}
                  onChange={(e) => setForm((f) => ({ ...f, entryKey: e.target.value }))}
                  placeholder="kebab-slug-key"
                  disabled={!!editing}
                />
              </div>

              <div>
                <label
                  htmlFor={`${fieldId}-category`}
                  className="text-label text-secondary block mb-1"
                >
                  Category
                </label>
                <Input
                  id={`${fieldId}-category`}
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="assistant-usage"
                />
              </div>

              <fieldset className="min-w-0">
                <legend className="text-label text-secondary block mb-1 p-0">
                  Question variants
                </legend>
                {form.questionVariants.map((v, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <Input
                      aria-label={`Question variant ${i + 1}`}
                      value={v}
                      onChange={(e) => handleVariantChange(i, e.target.value)}
                      placeholder="How do I…"
                      className="flex-1"
                    />
                    {form.questionVariants.length > 1 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeVariant(i)}
                        aria-label={`Remove question variant ${i + 1}`}
                      >
                        ✕
                      </Button>
                    )}
                  </div>
                ))}
                <Button variant="link" size="sm" onClick={addVariant}>
                  + Add variant
                </Button>
              </fieldset>

              <div>
                <label
                  htmlFor={`${fieldId}-answer`}
                  className="text-label text-secondary block mb-1"
                >
                  Answer
                </label>
                <Textarea
                  id={`${fieldId}-answer`}
                  value={form.answer}
                  onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                  rows={4}
                  placeholder="The canonical answer…"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label
                    htmlFor={`${fieldId}-tier`}
                    className="text-label text-secondary block mb-1"
                  >
                    Confidence tier
                  </label>
                  <Select
                    value={form.confidenceTier}
                    onValueChange={(v) =>
                      setForm((f) => ({
                        ...f,
                        confidenceTier: v as 'high' | 'medium' | 'low',
                      }))
                    }
                  >
                    <SelectTrigger id={`${fieldId}-tier`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIERS.map((t) => (
                        <SelectItem key={t} value={t}>
                          {TIER_LABELS[t]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="w-32">
                  <label
                    htmlFor={`${fieldId}-threshold`}
                    className="text-label text-secondary block mb-1"
                  >
                    Threshold
                  </label>
                  <Input
                    id={`${fieldId}-threshold`}
                    type="number"
                    min={0}
                    max={1}
                    step={0.05}
                    value={form.confidenceThreshold}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, confidenceThreshold: Number(e.target.value) }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="ghost"
                onClick={() => {
                  setFormOpen(false);
                  setEditing(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => upsertMutation.mutate(form)}
                disabled={
                  upsertMutation.isPending ||
                  !form.entryKey.trim() ||
                  !form.answer.trim() ||
                  form.questionVariants.every((v) => !v.trim())
                }
              >
                {upsertMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create entry'}
              </Button>
            </div>

            {upsertMutation.isError && (
              <p className="text-feedback-error text-body-sm mt-2">
                Save failed. Please try again.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
